import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { PlayArrow, Stop, Add, Send } from '@mui/icons-material';
import { getApiUrl } from '../../utils/apiConfig';
import { useAuth } from '../../context/AuthContext';
import DienstplanKalender from '../../components/DienstplanKalender';

// Tasks-Reiter bewusst ausgeblendet; Task-Auswahl bleibt im Heute-Reiter verfügbar
const tabConfig = [
  { value: 'dienstplan', label: 'Dienstplan' },
  { value: 'status', label: 'Heute' },
  { value: 'bookings', label: 'Zeiten' },
  { value: 'absences', label: 'Abwesenheit' }
];

function getTabFromPathname(pathname) {
  if (pathname.endsWith('/heute')) return 'status';
  if (pathname.endsWith('/zeitbuchungen')) return 'bookings';
  if (pathname.endsWith('/abwesenheiten')) return 'absences';
  return 'dienstplan';
}

const initialBookingForm = {
  todo_id: '',
  taetigkeit: '',
  wirksam_am: new Date().toISOString().slice(0, 10),
  start_ts: '',
  end_ts: '',
  dauer_minuten: '',
  pause_minuten: 0,
  notiz: ''
};

const initialAbsenceForm = {
  typ: 'urlaub',
  datum_von: new Date().toISOString().slice(0, 10),
  datum_bis: new Date().toISOString().slice(0, 10),
  halber_tag: false,
  kommentar: ''
};

export default function MaPortalPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(getTabFromPathname(location.pathname));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [quickBooking, setQuickBooking] = useState({
    wirksam_am: new Date().toISOString().slice(0, 10),
    dauer_minuten: '',
    notiz: ''
  });
  const [bookingForm, setBookingForm] = useState(initialBookingForm);
  const [absenceForm, setAbsenceForm] = useState(initialAbsenceForm);
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    newPin: ''
  });
  const [securityMessage, setSecurityMessage] = useState({ text: '', type: 'success' });
  const [dienstplan, setDienstplan] = useState({ schichten: [], meine: [] });
  const [dienstplanLoading, setDienstplanLoading] = useState(false);

  const api = useMemo(() => axios.create({ withCredentials: true }), []);

  useEffect(() => {
    setTab(getTabFromPathname(location.pathname));
  }, [location.pathname]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: response } = await api.get(getApiUrl('/api/ma/status'));
      if (!response?.success) {
        throw new Error(response?.error || 'Fehler beim Laden');
      }
      setData(response);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Fehler beim Laden des MA-Portals');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadDienstplan = useCallback(async () => {
    setDienstplanLoading(true);
    setError('');
    try {
      const { data: response } = await api.get(getApiUrl('/api/ma/dienstplan'));
      if (!response?.success) throw new Error(response?.error || 'Dienstplan konnte nicht geladen werden');
      setDienstplan({ schichten: response.schichten || [], meine: response.meine_schichten || [] });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Dienstplan konnte nicht geladen werden');
    } finally {
      setDienstplanLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (tab === 'dienstplan') loadDienstplan();
  }, [tab, loadDienstplan]);

  const handleDienstplanAnmelden = async (schicht, von, bis) => {
    setSaving(true);
    setError('');
    try {
      const { data: response } = await api.post(getApiUrl('/api/ma/dienstplan/anmeldung'), { schicht_id: schicht.schicht_id, zeit_von: von, zeit_bis: bis });
      if (!response?.success) throw new Error(response?.error || 'Aktion fehlgeschlagen');
      setDienstplan({ schichten: response.schichten || [], meine: response.meine_schichten || [] });
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Aktion fehlgeschlagen');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDienstplanAbmelden = async (schicht) => {
    setSaving(true);
    setError('');
    try {
      const { data: response } = await api.post(getApiUrl('/api/ma/dienstplan/abmeldung'), { schicht_id: schicht.schicht_id });
      if (!response?.success) throw new Error(response?.error || 'Aktion fehlgeschlagen');
      setDienstplan({ schichten: response.schichten || [], meine: response.meine_schichten || [] });
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Aktion fehlgeschlagen');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const tasks = data?.tasks || [];
    const activeTodoId = data?.timer?.todo_id ? String(data.timer.todo_id) : '';
    if (activeTodoId) {
      setSelectedTaskId(activeTodoId);
      return;
    }
    if (!tasks.length) {
      setSelectedTaskId('');
      return;
    }
    if (!selectedTaskId || !tasks.some((task) => String(task.id) === String(selectedTaskId))) {
      setSelectedTaskId(String(tasks[0].id));
    }
  }, [data, selectedTaskId]);

  const goToTab = (value) => {
    setTab(value);
    const pathByTab = { dienstplan: '/ma', status: '/ma/heute', bookings: '/ma/zeitbuchungen', absences: '/ma/abwesenheiten' };
    navigate(pathByTab[value] || '/ma', { replace: true });
  };

  const handleStartTimer = async () => {
    const selectedTask = (data?.tasks || []).find((task) => String(task.id) === String(selectedTaskId));
    if (!selectedTask) {
      setError('Bitte zuerst einen zugewiesenen Task auswählen');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post(getApiUrl('/api/ma/zeitbuchungen/timer/start'), {
        todo_id: Number(selectedTask.id),
        wirksam_am: new Date().toISOString().slice(0, 10),
        taetigkeit: selectedTask.titel || bookingForm.taetigkeit || 'Arbeitszeit'
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Timer konnte nicht gestartet werden');
    } finally {
      setSaving(false);
    }
  };

  const handleStopTimer = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post(getApiUrl('/api/ma/zeitbuchungen/timer/stop'), {});
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Timer konnte nicht gestoppt werden');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickBooking = async () => {
    const selectedTask = (data?.tasks || []).find((task) => String(task.id) === String(selectedTaskId));
    if (!selectedTask) {
      setError('Bitte zuerst einen zugewiesenen Task auswählen');
      return;
    }
    const minutes = Number(quickBooking.dauer_minuten || 0);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setError('Bitte eine gültige Dauer in Minuten eingeben');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await api.post(getApiUrl('/api/ma/zeitbuchungen'), {
        todo_id: Number(selectedTask.id),
        wirksam_am: quickBooking.wirksam_am,
        dauer_minuten: Math.round(minutes),
        taetigkeit: selectedTask.titel || 'Arbeitszeit',
        notiz: (quickBooking.notiz || '').trim(),
        quelle: 'manual'
      });
      setQuickBooking((prev) => ({ ...prev, dauer_minuten: '', notiz: '' }));
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Zeitbuchung konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleBookingSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...bookingForm,
        todo_id: bookingForm.todo_id ? Number(bookingForm.todo_id) : null,
        pause_minuten: bookingForm.pause_minuten === '' ? 0 : Number(bookingForm.pause_minuten),
        dauer_minuten: bookingForm.dauer_minuten === '' ? null : Number(bookingForm.dauer_minuten),
        taetigkeit: bookingForm.taetigkeit.trim(),
        notiz: bookingForm.notiz.trim(),
      };
      await api.post(getApiUrl('/api/ma/zeitbuchungen'), payload);
      setBookingForm(initialBookingForm);
      await loadData();
      goToTab('bookings');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Zeitbuchung konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleAbsenceSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post(getApiUrl('/api/ma/abwesenheiten'), {
        ...absenceForm,
        kommentar: absenceForm.kommentar.trim()
      });
      setAbsenceForm(initialAbsenceForm);
      await loadData();
      goToTab('absences');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Abwesenheit konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleSecurityUpdate = async (event) => {
    event.preventDefault();
    setError('');
    setSecurityMessage({ text: '', type: 'success' });

    const currentPassword = String(securityForm.currentPassword || '');
    const newPassword = String(securityForm.newPassword || '');
    const confirmNewPassword = String(securityForm.confirmNewPassword || '');
    const newPin = String(securityForm.newPin || '').trim();

    if (!currentPassword) {
      setSecurityMessage({ text: 'Bitte aktuelles Passwort eingeben.', type: 'error' });
      return;
    }
    if (!newPassword && !newPin) {
      setSecurityMessage({ text: 'Bitte neues Passwort oder neue PIN angeben.', type: 'error' });
      return;
    }
    if (newPassword && newPassword.length < 6) {
      setSecurityMessage({ text: 'Neues Passwort muss mindestens 6 Zeichen lang sein.', type: 'error' });
      return;
    }
    if (newPassword && newPassword !== confirmNewPassword) {
      setSecurityMessage({ text: 'Neue Passwörter stimmen nicht überein.', type: 'error' });
      return;
    }
    if (newPin && !/^\d{6}$/.test(newPin)) {
      setSecurityMessage({ text: 'Neue PIN muss aus genau 6 Ziffern bestehen.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        currentPassword,
        newPassword: newPassword || undefined,
        newPin: newPin || undefined
      };
      const { data: response } = await api.post(getApiUrl('/api/ma/zugangsdaten'), payload);
      if (!response?.success) {
        throw new Error(response?.error || 'Aktualisierung fehlgeschlagen');
      }
      setSecurityForm({ currentPassword: '', newPassword: '', confirmNewPassword: '', newPin: '' });
      setSecurityMessage({ text: 'Zugangsdaten erfolgreich aktualisiert.', type: 'success' });
      await loadData();
    } catch (err) {
      setSecurityMessage({ text: err?.response?.data?.error || err?.message || 'Aktualisierung fehlgeschlagen', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading && !data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeTimer = data?.timer || null;
  const selectedTask = (data?.tasks || []).find((task) => String(task.id) === String(selectedTaskId));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)', p: { xs: 2, md: 4 } }}>
      <Paper elevation={0} sx={{ maxWidth: 1200, mx: 'auto', borderRadius: 4, p: { xs: 2, md: 3 }, border: '1px solid #e2e8f0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Mitarbeiter-Portal</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Hallo {user?.username || 'Mitarbeiter'}</Typography>
            <Typography variant="body2" color="text.secondary">Nur Zeitbuchung, eigene Tasks und Abwesenheiten sind sichtbar.</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={user?.effective_role || user?.role || 'zeiterfasser'} color="primary" />
            <Button variant="outlined" onClick={loadData} disabled={saving}>Aktualisieren</Button>
            <Button variant="text" onClick={handleLogout}>Abmelden</Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Tabs value={tab} onChange={(e, value) => goToTab(value)} sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}>
          {tabConfig.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
        </Tabs>

        {tab === 'status' && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                <Box>
                  <Typography variant="h6">Aktueller Status</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {data?.profil ? `${data.profil.vorname} ${data.profil.nachname}` : 'Kein Mitarbeiterprofil gefunden'}
                  </Typography>
                  <TextField
                    select
                    label="Zugewiesener Task"
                    value={selectedTaskId}
                    onChange={(e) => setSelectedTaskId(e.target.value)}
                    sx={{ mt: 2, minWidth: { xs: '100%', md: 420 } }}
                    SelectProps={{ native: true }}
                    fullWidth
                  >
                    {(data?.tasks || []).length === 0 ? <option value="">Keine zugewiesenen Tasks</option> : null}
                    {(data?.tasks || []).map((task) => (
                      <option key={task.id} value={task.id}>{task.titel}</option>
                    ))}
                  </TextField>
                  {selectedTask ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Status: {selectedTask.status || 'offen'}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" startIcon={<PlayArrow />} onClick={handleStartTimer} disabled={saving || Boolean(activeTimer) || !selectedTask}>
                    Start
                  </Button>
                  <Button variant="outlined" startIcon={<Stop />} onClick={handleStopTimer} disabled={saving || !activeTimer}>
                    Ende
                  </Button>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <TextField
                  label="Datum"
                  type="date"
                  value={quickBooking.wirksam_am}
                  onChange={(e) => setQuickBooking((prev) => ({ ...prev, wirksam_am: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Dauer (Minuten)"
                  type="number"
                  value={quickBooking.dauer_minuten}
                  onChange={(e) => setQuickBooking((prev) => ({ ...prev, dauer_minuten: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Notiz"
                  value={quickBooking.notiz}
                  onChange={(e) => setQuickBooking((prev) => ({ ...prev, notiz: e.target.value }))}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleQuickBooking} disabled={saving || !selectedTask}>
                  Zeit buchen
                </Button>
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Laufender Timer</Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                    {activeTimer ? 'Aktiv' : 'Inaktiv'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeTimer ? `Seit ${new Date(activeTimer.start_ts).toLocaleString('de-DE')}` : 'Kein offener Timer'}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Offene Aufgaben</Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{data?.tasks?.length || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Zugewiesene Tasks</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Zeiteinträge</Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{data?.zeitbuchungen?.length || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Eigene Buchungen</Typography>
                </CardContent>
              </Card>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleSecurityUpdate}>
              <Stack spacing={2}>
                <Typography variant="h6">Sicherheit: Passwort und PIN ändern</Typography>
                <Typography variant="body2" color="text.secondary">
                  Zum Ändern von Passwort und/oder Zeiterfassungs-PIN ist immer das aktuelle Passwort erforderlich.
                </Typography>
                {securityMessage.text ? (
                  <Alert severity={securityMessage.type === 'error' ? 'error' : 'success'}>
                    {securityMessage.text}
                  </Alert>
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Aktuelles Passwort"
                    type="password"
                    value={securityForm.currentPassword}
                    onChange={(e) => setSecurityForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Neue PIN (6-stellig, optional)"
                    value={securityForm.newPin}
                    onChange={(e) => setSecurityForm((prev) => ({ ...prev, newPin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Neues Passwort (optional)"
                    type="password"
                    value={securityForm.newPassword}
                    onChange={(e) => setSecurityForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Neues Passwort wiederholen"
                    type="password"
                    value={securityForm.confirmNewPassword}
                    onChange={(e) => setSecurityForm((prev) => ({ ...prev, confirmNewPassword: e.target.value }))}
                    fullWidth
                  />
                </Stack>
                <Button type="submit" variant="contained" disabled={saving}>
                  Zugangsdaten speichern
                </Button>
              </Stack>
            </Paper>
          </Stack>
        )}

        {tab === 'dienstplan' && (
          <Paper variant="outlined" sx={{ p: 2, maxWidth: 560, mx: 'auto' }}>
            {dienstplanLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={2}>
                <Typography variant="h6">Dienstplan</Typography>
                <Typography variant="body2" color="text.secondary">
                  Tippe auf einen freigeschalteten Tag und wähle eine Schicht, um dich ein- oder auszutragen.
                </Typography>
                <DienstplanKalender
                  schichten={dienstplan.schichten}
                  meineSchichten={dienstplan.meine}
                  canEdit
                  submitting={saving}
                  onAnmelden={handleDienstplanAnmelden}
                  onAbmelden={handleDienstplanAbmelden}
                />
              </Stack>
            )}
          </Paper>
        )}

        {tab === 'bookings' && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleBookingSubmit}>
              <Stack spacing={2}>
                <Typography variant="h6">Neue Zeitbuchung</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Task-ID (optional)" value={bookingForm.todo_id} onChange={(e) => setBookingForm((prev) => ({ ...prev, todo_id: e.target.value }))} fullWidth />
                  <TextField label="Datum" type="date" value={bookingForm.wirksam_am} onChange={(e) => setBookingForm((prev) => ({ ...prev, wirksam_am: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label="Pause (Minuten)" type="number" value={bookingForm.pause_minuten} onChange={(e) => setBookingForm((prev) => ({ ...prev, pause_minuten: e.target.value }))} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Tätigkeit" value={bookingForm.taetigkeit} onChange={(e) => setBookingForm((prev) => ({ ...prev, taetigkeit: e.target.value }))} fullWidth />
                  <TextField label="Notiz" value={bookingForm.notiz} onChange={(e) => setBookingForm((prev) => ({ ...prev, notiz: e.target.value }))} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Start (optional)" type="datetime-local" value={bookingForm.start_ts} onChange={(e) => setBookingForm((prev) => ({ ...prev, start_ts: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label="Ende (optional)" type="datetime-local" value={bookingForm.end_ts} onChange={(e) => setBookingForm((prev) => ({ ...prev, end_ts: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label="Dauer (Minuten)" type="number" value={bookingForm.dauer_minuten} onChange={(e) => setBookingForm((prev) => ({ ...prev, dauer_minuten: e.target.value }))} fullWidth />
                </Stack>
                <Button type="submit" variant="contained" startIcon={<Add />} disabled={saving}>Speichern</Button>
              </Stack>
            </Paper>

            {(data?.zeitbuchungen || []).map((entry) => (
              <Paper key={entry.zeitbuchung_id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{entry.todo_titel || entry.taetigkeit || 'Zeitbuchung'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entry.wirksam_am} · {entry.dauer_minuten || 0} Minuten · {entry.status}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={entry.quelle || 'manual'} />
                    <Chip size="small" variant="outlined" label={`Pause ${entry.pause_minuten || 0} min`} />
                  </Stack>
                </Stack>
              </Paper>
            ))}
            {(data?.zeitbuchungen || []).length === 0 ? <Alert severity="info">Noch keine Zeitbuchungen vorhanden.</Alert> : null}
          </Stack>
        )}

        {tab === 'absences' && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }} component="form" onSubmit={handleAbsenceSubmit}>
              <Stack spacing={2}>
                <Typography variant="h6">Abwesenheit erfassen</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField select label="Typ" value={absenceForm.typ} onChange={(e) => setAbsenceForm((prev) => ({ ...prev, typ: e.target.value }))} fullWidth SelectProps={{ native: true }}>
                    <option value="urlaub">Urlaub</option>
                    <option value="krank">Krank</option>
                    <option value="unbezahlt">Unbezahlt</option>
                    <option value="sonstiges">Sonstiges</option>
                  </TextField>
                  <TextField label="Von" type="date" value={absenceForm.datum_von} onChange={(e) => setAbsenceForm((prev) => ({ ...prev, datum_von: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label="Bis" type="date" value={absenceForm.datum_bis} onChange={(e) => setAbsenceForm((prev) => ({ ...prev, datum_bis: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                </Stack>
                <TextField label="Kommentar" value={absenceForm.kommentar} onChange={(e) => setAbsenceForm((prev) => ({ ...prev, kommentar: e.target.value }))} fullWidth />
                <Button type="submit" variant="contained" startIcon={<Send />} disabled={saving}>Abwesenheit speichern</Button>
              </Stack>
            </Paper>

            {(data?.abwesenheiten || []).map((absence) => (
              <Paper key={absence.abwesenheit_id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{absence.typ}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {absence.datum_von} - {absence.datum_bis} · {absence.status}
                    </Typography>
                  </Box>
                  <Chip size="small" variant="outlined" label={absence.halber_tag ? 'Halber Tag' : 'Ganztägig'} />
                </Stack>
              </Paper>
            ))}
            {(data?.abwesenheiten || []).length === 0 ? <Alert severity="info">Noch keine Abwesenheiten vorhanden.</Alert> : null}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
